import { get } from 'lodash';
import * as rules from './rules';
import { flattenObject, isEmpty, isValidDate } from './utils';
let missedRuleValidator = function () {
    throw new Error('Validator `' + this.name + '` is not defined!');
};
let missedRuleMessage = '';
export class Rule {
    async;
    _customMessage;
    passes;
    fn;
    name;
    callback;
    attribute;
    input;
    rule;
    validator;
    static rules = Object.assign({}, rules);
    constructor(name, fn, async) {
        this.name = name;
        this.fn = fn;
        this.passes = false;
        this._customMessage = undefined;
        this.async = async;
        Rule._setRules();
        this.attribute = '';
    }
    validate(input, rule, attribute = '', callback = null) {
        this._setValidatingData(attribute, input, rule);
        if (typeof callback === 'function') {
            this.callback = callback;
            const handleResponse = (passes, message) => {
                return this.response(passes, message);
            };
            if (this.async) {
                return this._apply(input, rule, attribute, handleResponse);
            }
            else {
                return handleResponse(this._apply(input, rule, attribute));
            }
        }
        return this._apply(input, rule, attribute);
    }
    _apply(input, rule, attribute, callback = null) {
        const fn = this.isMissed() ? missedRuleValidator : this.fn;
        return fn.apply(this, [input, rule, attribute, callback]);
    }
    _setValidatingData(attribute, input, rule) {
        this.attribute = attribute;
        this.input = input;
        this.rule = rule;
    }
    getParameters() {
        let value = [];
        if (!isNaN(parseFloat(this.rule)) && isFinite(this.rule)) {
            this.rule = parseFloat(this.rule);
            value.push(this.rule);
        }
        if (typeof this.rule === 'string') {
            value = this.rule.split(',');
        }
        if (this.rule instanceof Array) {
            value = this.rule;
        }
        return value;
    }
    getSize(value) {
        const input = value || this.input;
        if (input instanceof Array) {
            return input.length;
        }
        if (typeof input === 'number') {
            return input;
        }
        if (this.validator._hasNumericRule(this.attribute)) {
            return parseFloat(input);
        }
        return input.length;
    }
    _getValueType() {
        if (typeof this.input === 'number' || this.validator._hasNumericRule(this.attribute)) {
            return 'numeric';
        }
        return 'string';
    }
    response(passes, message) {
        this.passes = passes === undefined || passes;
        this._customMessage = message;
        this.callback(this.passes, message);
    }
    setValidator(validator) {
        this.validator = validator;
    }
    isMissed() {
        return typeof this.fn !== 'function';
    }
    get customMessages() {
        return this.isMissed() ? missedRuleMessage : this._customMessage;
    }
    static _setRules() {
        Rule.rules = {
            ...this.rules,
            after(value, req) {
                const val1 = this.validator.input[req];
                const val2 = value;
                if (!isValidDate(val1) || !isValidDate(val2)) {
                    return false;
                }
                return new Date(val1).getTime() < new Date(val2).getTime();
            },
            after_or_equal(val, req) {
                const val1 = this.validator.input[req];
                const val2 = val;
                if (!isValidDate(val1) || !isValidDate(val2))
                    return false;
                return new Date(val1).getTime() <= new Date(val2).getTime();
            },
            required_if(val, req) {
                req = this.getParameters();
                if (get(this.validator.input, req[0]) === req[1]) {
                    return this.validator.getRule('required').validate(val, {});
                }
                return true;
            },
            required_unless(val, req) {
                req = this.getParameters();
                if (get(this.validator.input, req[0]) !== req[1]) {
                    return this.validator.getRule('required').validate(val, {});
                }
                return true;
            },
            required_with(val, req) {
                if (get(this.validator.input, req)) {
                    return this.validator.getRule('required').validate(val, {});
                }
                return true;
            },
            required_with_all(val, req) {
                req = this.getParameters();
                for (const re of req) {
                    if (!get(this.validator.input, re))
                        return true;
                }
                return this.validator.getRule('required').validate(val, {});
            },
            required_without(val, req) {
                if (get(this.validator.input, req))
                    return true;
                return this.validator.getRule('required').validate(val, {});
            },
            required_without_all(val, req) {
                req = this.getParameters();
                for (const re of req) {
                    if (get(this.validator.input, re))
                        return true;
                }
                return this.validator.getRule('required').validate(val, {});
            },
            size(val, req) {
                if (!val)
                    return false;
                req = parseFloat(String(req));
                const size = this.getSize();
                return size === req;
            },
            min(val, req) {
                const size = this.getSize(val);
                return size >= req;
            },
            max(val, req) {
                const size = this.getSize(val);
                return size <= req;
            },
            between(val, req) {
                req = this.getParameters();
                const size = this.getSize();
                const min = parseFloat(req[0]);
                const max = parseFloat(req[1]);
                return size >= min && size <= max;
            },
            same(val, req) {
                const val1 = flattenObject(this.validator.input)[req];
                return val1 === val;
            },
            different(val, req) {
                const val1 = flattenObject(this.validator.input)[req];
                return val1 !== val;
            },
            in(val) {
                let list = [];
                if (!isEmpty(val))
                    list = this.getParameters();
                if (!isEmpty(val) && !(val instanceof Array)) {
                    let localValue = val;
                    for (const li of list) {
                        if (typeof li === 'string')
                            localValue = String(val);
                        if (localValue === li)
                            return true;
                    }
                    return false;
                }
                if (val && val instanceof Array) {
                    for (const va of val) {
                        if (list.indexOf(va) < 0)
                            return false;
                    }
                }
                return true;
            },
            not_in(val) {
                const list = this.getParameters();
                let returnVal = true;
                for (const li of list) {
                    let localValue = val;
                    if (typeof li === 'string')
                        localValue = String(val);
                    if (localValue === li) {
                        returnVal = false;
                        break;
                    }
                }
                return returnVal;
            },
            confirmed(val, req, attribute) {
                const confirmedKey = attribute + '_confirmation';
                return this.validator.input[confirmedKey] === val;
            },
            digits(val, req) {
                const numericRule = this.validator.getRule('numeric');
                return !!(numericRule.validate(val, {}) && String(val).trim().length === parseInt(req));
            },
            digits_between(val) {
                const numericRule = this.validator.getRule('numeric');
                const req = this.getParameters();
                const min = parseFloat(req[0]);
                const max = parseFloat(req[1]);
                return numericRule.validate(val, {}) && val >= min && val <= max;
            },
            before(val, req) {
                const val1 = this.validator.input[req];
                const val2 = val;
                if (!isValidDate(val1) || !isValidDate(val2))
                    return false;
                return new Date(val1).getTime() > new Date(val2).getTime();
            },
            before_or_equal(val, req) {
                const val1 = this.validator.input[req];
                const val2 = val;
                if (!isValidDate(val1) || !isValidDate(val2))
                    return false;
                return new Date(val1).getTime() >= new Date(val2).getTime();
            },
            ip(val, req, attribute) {
                return Rule.rules['ipv4'](val, req, attribute) || Rule.rules['ipv6'](val, req, attribute);
            },
        };
    }
}
export class Manager {
    asyncRules = [];
    implicitRules = [
        'present',
        'required',
        'accepted',
        'required_if',
        'required_with',
        'required_unless',
        'required_without',
        'required_with_all',
        'required_without_all',
    ];
    make(name, validator) {
        Rule._setRules();
        const isAsync = this.isAsync(name);
        const rule = new Rule(name, Rule.rules[name], isAsync);
        rule.setValidator(validator);
        return rule;
    }
    isAsync(name) {
        for (const rule of this.asyncRules) {
            if (rule === name) {
                return true;
            }
        }
        return false;
    }
    isImplicit(name) {
        return this.implicitRules.indexOf(name) > -1;
    }
    register(name, fn) {
        Rule.rules[name] = fn;
    }
    registerImplicit(name, fn) {
        this.register(name, fn);
        this.implicitRules.push(name);
    }
    registerAsync(name, fn) {
        this.register(name, fn);
        this.asyncRules.push(name);
    }
    registerAsyncImplicit(name, fn) {
        this.registerImplicit(name, fn);
        this.asyncRules.push(name);
    }
    registerMissedRuleValidator(fn, message) {
        missedRuleValidator = fn;
        missedRuleMessage = message;
    }
}
